import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BarChart } from './_finHelpers';

// CD Calculator — matches image
export default function CDCalc() {
  const [initial, setInitial] = useState(10000);
  const [rate, setRate] = useState(5);
  const [compound, setCompound] = useState(1);
  const [years, setYears] = useState(3);
  const [months, setMonths] = useState(0);
  const [tax, setTax] = useState(0);
  const compOpts = [
    { val: 1, label: 'annually (APY)' },
    { val: 2, label: 'semi-annually' },
    { val: 4, label: 'quarterly' },
    { val: 12, label: 'monthly' },
    { val: 52, label: 'weekly' },
    { val: 365, label: 'daily' },
    { val: 0, label: 'continuously' },
  ];
  const c = useMemo(() => {
    const t = years + months/12;
    let end;
    if (compound === 0) end = initial * Math.exp(rate/100 * t);
    else end = initial * Math.pow(1 + rate/100/compound, compound * t);
    const interest = (end - initial) * (1 - tax/100);
    const afterTax = initial + interest;
    // yearly schedule for bar graph
    const rows = [];
    let bal = initial;
    for (let y = 1; y <= Math.ceil(t); y++) {
      const dt = Math.min(1, t - (y-1));
      const yrs = y;
      let newBal;
      if (compound === 0) newBal = bal * Math.exp(rate/100 * dt);
      else newBal = bal * Math.pow(1 + rate/100/compound, compound * dt);
      const yInt = (newBal - bal) * (1 - tax/100);
      bal = bal + yInt;
      rows.push({ year: yrs, deposit: y === 1 ? initial : 0, interest: yInt, balance: bal });
    }
    return { end: afterTax, interest, rows };
  }, [initial, rate, compound, years, months, tax]);
  return (
    <CalcShell look="cnet" title="CD Calculator" subtitle="Compute the accumulated interest and end balance for a Certificate of Deposit."
      about={<><p>A <b>Certificate of Deposit (CD)</b> is a time-deposit account offered by banks — usually paying a fixed interest rate for a specific term.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Initial deposit</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label><select value={compound} onChange={e=>setCompound(+e.target.value)}>{compOpts.map(o=><option key={o.val} value={o.val}>{o.label}</option>)}</select></div>
          <div className="field"><label>Deposit length</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="field"><label>Marginal tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={tax} onChange={e=>setTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">End balance</div><div className="result-value" data-testid="cd-end">{fmt(c.end)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Initial deposit',value:initial,color:'#2563eb'},{label:'Interest',value:c.interest,color:'#10b981'}]} size={180}/></div>
        </div>
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Balance Accumulation Graph</h3>
        <BarChart
          data={c.rows.map(r => ({
            label: String(r.year),
            values: [
              { value: initial, color: '#2563eb' },
              { value: Math.max(0, r.balance - initial), color: '#f97316' },
            ]
          }))}
          stack={true}
          legend={[
            { label: 'Initial deposit', color: '#2563eb' },
            { label: 'Interest earned', color: '#f97316' },
          ]}
        />
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Accumulation Schedule</h3>
        <table className="w-full text-sm">
          <thead className="bg-blue-800 text-white"><tr><th className="px-3 py-2 text-left">Year</th><th className="px-3 py-2 text-right">Deposit</th><th className="px-3 py-2 text-right">Interest</th><th className="px-3 py-2 text-right">Ending balance</th></tr></thead>
          <tbody>
            {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{r.year}</td><td className="px-3 py-2 text-right">{fmt(r.deposit)}</td><td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.balance)}</td></tr>))}
          </tbody>
        </table>
      </div>
    </CalcShell>
  );
}
